<template>
  <div class="policy" :class="[
    { 'animate__animated animate__zoomOutLeft': isDeleting },
    { deleting: wasDeleted },
    `offer-${offerTypeColor}`,
  ]">
    <div class="row row--policy box box--default">
      <div class="row__data row__data--small justify-content-center">{{ createdDate }}</div>
      <div class="row__data row__data--medium">{{ offer.policy_offer_name }}</div>
      <div class="row__data row__data--small justify-content-center">
        <div v-if="offer.inform_customer" class="icon icon__active"></div>
        <div v-else class="icon icon__inactive"></div> 
      </div>
      <!-- <div class="row__data row__data--small justify-content-center">{{ offer.status.name }}</div> -->
      <div class="row__data row__data--medium justify-content-center">{{ user }}</div>
      <div class="row__data row__data--medium justify-content-center">{{ agent }}</div>
      <div class="row__data row__data--small justify-content-center">

        <div @click="$store.dispatch('reSubmitCustomerOffer', offer)" v-if="!offer.submitted_at && offer.status.id == 3"
          class="icon icon__mail"></div>
        <span v-else>{{ submittedAt }}</span>
      </div>

      <div class="row__data row__data--smaller row__data--pdf"
        :class="offer.files && offer.files.length ? 'icon__pdf--active' : 'icon__pdf'"
        @click="openOfferFilesModal(offer)"></div>

      <div class="row__data row__data--small row__data--edit icon__edit" @click="openOfferModal(offer)"></div>

      <div class="row__data row__data--small row__data--delete icon__delete"
        :class="{ 'icon__delete--active': activeOfferId === offer.id }" tabindex="-1" @focus="activeOfferId = offer.id"
        @focusout="activeOfferId = null" :ref="'delete-' + offer.id">
        <transition name="fade">
          <div class="delete" v-if="activeOfferId == offer.id">
            <div class="delete__text">
              {{ $t(`offersTable.deleteOfferConfirmation`) }}
            </div>
            <div class="delete__buttons">
              <div class="button button--confirm" @click="deleteOffer(offer.id)"></div>
              <div class="button button--cancel" @click="closeOfferDeletePopup"></div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { formatDisplayDate } from "@/helpers/dateFormatters";
import statics from "@/statics";

defineOptions({
  name: "Offer"
});

const props = defineProps({
  offer: {
    required: true,
    type: Object,
  },
  index: {
    required: true,
    type: Number,
  },
});

const store = useStore();
const route = useRoute();

// State
const isDeleting = ref(false);
const wasDeleted = ref(false);
const activeOfferId = ref(null);

const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const offerTeamId = computed(() => props.offer?.team_id ?? props.offer?.team?.id ?? store.getters.customer?.team_id ?? store.getters.customer?.team?.id ?? null);
const canEditOffer = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("customer_policy_offer_edit", offerTeamId.value);
});
const canDeleteOffer = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("customer_policy_offer_delete", offerTeamId.value);
});
const canAccessFiles = computed(() => {
  if (isSuperAdmin.value) return true;
  return store.getters.hasTeamPermission("customer_policy_offer_access", offerTeamId.value);
});

// Template Refs
// Note: In Vue 3, using dynamic refs (e.g., ref="delete-id") is generally discouraged. 
// If you need to blur elements, consider using a scoped ref/function approach.
const deleteRefs = ref({});

// Computed
const createdDate = computed(() =>
  props.offer.created_at ? formatDisplayDate(props.offer.created_at) : statics.empty
);

const submittedAt = computed(() =>
  props.offer.submitted_at ? formatDisplayDate(props.offer.submitted_at) : statics.empty
);

const user = computed(() =>
  props.offer.user ? `${props.offer.user.first_name} ${props.offer.user.last_name}` : statics.empty
);

const agent = computed(() =>
  props.offer.agent ? `${props.offer.agent.first_name} ${props.offer.agent.last_name}` : statics.empty
);

const offerTypeColor = computed(() =>
  props.offer.status ? `offer-${props.offer.status.id}` : null
);

// Methods
const closeOfferDeletePopup = (offerId) => {
  const deleteRef = deleteRefs.value[offerId];
  if (deleteRef) {
    deleteRef.blur();
  }
  activeOfferId.value = null;
};

const deleteOffer = async (offerId) => {
  if (!canDeleteOffer.value) return;

  isDeleting.value = true;
  await store.dispatch("deleteCustomerOffer", { offerId, customerId: route.params.id });
  wasDeleted.value = true;
  setTimeout(async () => {
    await store.dispatch("getCustomerOffers", route.params.id);
  }, 300);
};

// const changeOfferAgent = async (agentId) => {
//   await store.dispatch("updateOfferAgent", {
//     offerId: props.offer.id,
//     agentId,
//   });
//   await store.dispatch("getCustomerOffers", route.params.id);
// };

// const updateOfferText = async (text) => {
//   await store.dispatch("updateOfferText", {
//     offerId: props.offer.id,
//     text,
//   });
//   await store.dispatch("getCustomerOffers", route.params.id);
// };

// const downloadPdf = (offerId) => {
//   store.dispatch("downloadOfferDocument", offerId);
// };

const openOfferFilesModal = (offer) => {
  if (!canAccessFiles.value) return;

  const files = [{
    id: `${offer.policy_offer_code}-Offerte_${offer.id}`,
    name: `${offer.policy_offer_code}-Offerte.pdf`,
    file_name: `${offer.policy_offer_code}-Offerte.pdf`,
    created_at: offer.created_at,
    isDownloadable: true,
    offerId: offer.id,
  }];

  if (offer.files && Array.isArray(offer.files)) {
    files.push(...offer.files);
  }

  store.commit("setFilesToEditId", offer.id);
  store.commit("setFilesToEdit", files);
  store.commit("setFilesEditMode", 'Offers');
  store.commit("setShowFilesEditModal", true);
};

const openOfferModal = async (offer) => {
  if (!canEditOffer.value) return;

  store.commit("setOfferToEdit", null);
  store.commit("setShowAddOfferModal", true);
  await store.dispatch("getSpecificOffer", offer.id);
};
</script>

<style lang="scss" scoped>
.delete {
  position: absolute;
  z-index: 3;
  background: var(--bg-1);
  right: calc(3% + 5px);
  top: 25%;
  transform: translateY(-50%);
  width: 300px;
  display: flex;
  align-items: center;
  border-radius: var(--radius-1);
  border: 1px solid var(--border-2);
  padding: 10px 20px;
  box-shadow: 0px 3px 6px var(--shadow-7);

  &__text {
    line-height: 20px;
    color: var(--text-9);
  }

  &__buttons {
    display: flex;
  }

  .button {
    width: 24px;
    height: 24px;
    cursor: pointer;
    transition: all 0.3s ease;

    &--confirm {
      background: url("../../assets/icons/default/confirm_icon.svg");
      margin: 0 15px;

      &:hover {
        background: url("../../assets/icons/default/confirm_hover_icon.svg");
      }
    }

    &--cancel {
      background: url("../../assets/icons/default/close_icon.svg");

      &:hover {
        background: url("../../assets/icons/default/close_hover_icon.svg");
      }
    }
  }
}

.offer-offer-1::before {
  background-color: #ff3333;
}

.offer-offer-3::before {
  background-color: #FF9E0F;
}

.offer-offer-2::before {
  background-color: #22db8e;
}

.justify-content-center {
  justify-content: center !important;
}

.policy .row__data:first-of-type {
  padding: 0 20px !important;
  padding-right: 5px !important;
}

.row__data--smaller {
  padding: 0 25px !important;
}
</style>
